// Chip selector — used in mobile Add Personnel for designation/crew. Each
// option renders as a pill; the selected one gets primaryBg + gold text.
// Single-select only in V1 (mobile is also single-select per-field).
//
// Labels: underscores become spaces and each word is title-cased (e.g.
// 'near_capacity' → 'Near Capacity'). All-uppercase tokens (e.g. 'CRITICAL',
// 'NAG') are preserved as-is since title-casing them would change meaning.

import clsx from 'clsx'

interface ChipSelectorProps<T extends string> {
  label: string
  options: readonly T[]
  value: T | null
  onChange: (value: T) => void
  error?: string | null
}

function formatChipLabel(opt: string): string {
  // Preserve all-uppercase tokens like 'CRITICAL', 'HIGH', 'NAG'.
  if (opt === opt.toUpperCase()) return opt.replace(/_/g, ' ')
  return opt
    .replace(/_/g, ' ')
    .split(' ')
    .map(w => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
}

export function ChipSelector<T extends string>({
  label,
  options,
  value,
  onChange,
  error,
}: ChipSelectorProps<T>) {
  return (
    <div className="flex flex-col gap-xs">
      <label className="text-sm text-textSecondary">{label}</label>
      <div className="flex flex-wrap gap-xs">
        {options.map(opt => {
          const selected = value === opt
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={clsx(
                'rounded-md border px-md py-sm text-sm transition-colors',
                selected
                  ? 'border-primary bg-primaryBg font-semibold text-primary'
                  : 'border-surfaceBorder bg-surfaceLight text-textSecondary hover:border-primary',
              )}
            >
              {formatChipLabel(opt)}
            </button>
          )
        })}
      </div>
      {error && <span className="text-xs text-statusRed">{error}</span>}
    </div>
  )
}
